import {
  User, Target, Calendar, CheckCircle2, AlertTriangle
} from 'lucide-react';
import type { Goal } from '../../types';
import {
  getGoalStatusColor, getGoalPriorityColor, getGoalCategoryLabel 
} from '../../types';

interface GoalCardProps {
  readonly goal: Goal;
  readonly onClick?: () => void;
}

function getProgressBarColor(status: string): string {
  if (status === 'completed') return 'bg-green-500';
  if (status === 'at-risk') return 'bg-red-500';
  return 'bg-blue-500';
}

function DueDateDisplay({
  goal,
  isOverdue,
  formattedTargetDate,
}: {
  readonly goal: Goal;
  readonly isOverdue: boolean;
  readonly formattedTargetDate: string;
}) {
  if (goal.completedAt) {
    return (
      <>
        <CheckCircle2 className="h-3 w-3 text-green-600" />
        Completed
      </>
    );
  }
  
  if (isOverdue) {
    return (
      <>
        <AlertTriangle className="h-3 w-3" />
        Overdue
      </>
    );
  }
  
  return (
    <>
      <Calendar className="h-3 w-3" />
      Due: {formattedTargetDate}
    </>
  );
}

export function GoalCard({
  goal, onClick 
}: GoalCardProps) {
  const statusColor = getGoalStatusColor(goal.status);
  const priorityColor = getGoalPriorityColor(goal.priority);
  const categoryLabel = getGoalCategoryLabel(goal.category);
  
  const targetDate = new Date(goal.targetDate);
  const isOverdue = goal.status !== 'completed' && goal.status !== 'cancelled' && targetDate < new Date();
  
  const formattedTargetDate = targetDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const completedMilestones = goal.milestones?.filter(m => m.completed).length ?? 0;
  const totalMilestones = goal.milestones?.length ?? 0;
  const progressBarColor = getProgressBarColor(goal.status);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-lg border bg-white p-4 text-left transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-2">
            <Target className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{goal.title}</h3>
            <p className="text-sm text-gray-500">{goal.employeeName} • {goal.employeeRole}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusColor}`}>
            {goal.status.replace('-', ' ')}
          </span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${priorityColor}`}>
            {goal.priority}
          </span>
        </div>
      </div>

      <p className="mt-3 line-clamp-2 text-sm text-gray-600">{goal.description}</p>

      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>Progress</span>
          <span className="font-medium">{goal.progress}%</span>
        </div>
        <div className="h-2 w-full rounded-full bg-gray-100">
          <div
            className={`h-2 rounded-full transition-all ${progressBarColor}`}
            style={{ width: `${goal.progress}%` }}
          />
        </div>
      </div>

      {goal.milestones && goal.milestones.length > 0 && (
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
          <CheckCircle2 className="h-3 w-3" />
          <span>{completedMilestones}/{totalMilestones} milestones completed</span>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <span className="rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-700">
            {categoryLabel}
          </span>
        </div>
        <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : ''}`}>
          <DueDateDisplay goal={goal} isOverdue={isOverdue} formattedTargetDate={formattedTargetDate} />
        </span>
      </div>

      <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
        <User className="h-3 w-3" />
        Assigned by {goal.assignedByName} • {goal.siteName}
      </div>
    </button>
  );
}
